class CollectionCarousel extends HTMLElement {
  constructor() {
    super()
    this.swiper = null
  }

  connectedCallback() {
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper.js is not loaded')
      return
    }

    const sectionId = this.dataset.sectionId
    const swiperElem = this.querySelector(`#collection-carousel-${sectionId}`)
    if (!swiperElem) return

    // Get navigation selectors from data attributes if provided
    const navigationPrev =
      this.dataset.navigationPrev || `.swiper-button-prev-${sectionId}`
    const navigationNext =
      this.dataset.navigationNext || `.swiper-button-next-${sectionId}`

    const allowMousewheel = this.dataset.allowMousewheel === 'true'
    const allowAutoplay = this.dataset.allowAutoplay === 'true'
    const playInLoop = this.dataset.playInLoop === 'true'
    const autoplayDelay = parseInt(this.dataset.autoplayDelay || '2500', 10)
    const autoplayDisableOnInteraction =
      this.dataset.autoplayDisableOnInteraction === 'true'

    let autoplay = undefined
    if (allowAutoplay) {
      autoplay = {
        delay: autoplayDelay,
        disableOnInteraction: autoplayDisableOnInteraction,
        forceToAxis: true,
        releaseOnEdges: true,
        sensitivity: 1,
      }
    }

    let breakpoints = {
      0: {
        slidesPerView: 1.5,
      },
      750: {
        slidesPerView: 2,
      },
      1100: {
        slidesPerView: 3,
      },
      1300: {
        slidesPerView: 4,
      },
    }

    const swiperConfig = {
      mousewheel: allowMousewheel,
      autoplay,
      loop: playInLoop,
      spaceBetween: 25,
      pagination: false,
      navigation: {
        nextEl: navigationNext,
        prevEl: navigationPrev,
      },
      breakpoints,
    }

    this.swiper = new Swiper(swiperElem, swiperConfig)

    // Check if we should disable mousewheel due to insufficient slides
    const checkMousewheelNeed = () => {
      if (!this.swiper || !allowMousewheel) return false

      const slideCount = this.swiper.slides.length
      const currentSlidesPerView = this.swiper.params.slidesPerView

      // If we have fewer slides than what we're trying to show, disable mousewheel
      return slideCount > currentSlidesPerView
    }

    // Initial check and update mousewheel setting
    let shouldEnableMousewheel = checkMousewheelNeed()
    if (!shouldEnableMousewheel && this.swiper?.mousewheel) {
      this.swiper.mousewheel.disable()
    }

    // Re-check on window resize since breakpoints can change slidesPerView
    this.handleResize = () => {
      const newShouldEnable = checkMousewheelNeed()
      if (newShouldEnable !== shouldEnableMousewheel) {
        shouldEnableMousewheel = newShouldEnable
        if (this.swiper?.mousewheel) {
          if (shouldEnableMousewheel) {
            this.swiper.mousewheel.enable()
          } else {
            this.swiper.mousewheel.disable()
          }
        }
      }
    }

    window.addEventListener('resize', this.handleResize)

    // Better mousewheel handling to prevent getting stuck
    if (allowMousewheel && shouldEnableMousewheel) {
      let isHoveringCarousel = false

      // Track when mouse enters/leaves the carousel area
      this.addEventListener('mouseenter', () => {
        isHoveringCarousel = true
      })

      this.addEventListener('mouseleave', () => {
        isHoveringCarousel = false
        // Re-enable mousewheel when leaving the carousel
        if (this.swiper?.mousewheel) {
          this.swiper.mousewheel.enable()
        }
      })

      // Only disable mousewheel during page scroll if we're not hovering the carousel
      window.addEventListener('scroll', () => {
        if (!isHoveringCarousel && this.swiper?.mousewheel?.enabled) {
          this.swiper.mousewheel.disable()

          // Re-enable after scroll ends
          clearTimeout(this.scrollTimeout)
          this.scrollTimeout = setTimeout(() => {
            if (this.swiper?.mousewheel) {
              this.swiper.mousewheel.enable()
            }
          }, 150)
        }
      })
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }

    // Clear any pending timeouts
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }

    // Destroy swiper instance
    if (this.swiper && typeof this.swiper.destroy === 'function') {
      this.swiper.destroy(true, true)
    }
  }
}

customElements.define('collection-carousel', CollectionCarousel)
